import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { PRIMARY_NAVIGATION_HEIGHT_PX } from 'components/ui/PrimaryNavigation/PrimaryNavigation.styles';
import { getLegacyShellZoomCompensation, useZoom } from 'context/ZoomContext';
import { SHOW } from './season';
import { useWorkboardHalloweenState } from './useWorkboardHalloween';
import './workboardHalloween.css';

type WebCorner = 'tr' | 'bl' | 'br' | 'tl';

interface BatFlight {
  id: number;
  delay: number;
  duration: number;
  y0: number;
}

interface ExtraWeb {
  corner: Exclude<WebCorner, 'tr'>;
  days: number;
  size: number;
  degrees: number[];
  rings: number[];
}

const EXTRA_WEBS: readonly ExtraWeb[] = [
  { corner: 'bl', days: 5, size: 130, degrees: [90, 112, 133, 157, 180], rings: [0.2, 0.4, 0.6, 0.82] },
  { corner: 'br', days: 2, size: 115, degrees: [90, 105, 124, 140, 158, 180], rings: [0.18, 0.37, 0.58, 0.8] },
  { corner: 'tl', days: 0, size: 105, degrees: [90, 115, 138, 160, 180], rings: [0.22, 0.45, 0.7] },
];

const MIRROR: Record<Exclude<WebCorner, 'tr'>, string> = {
  bl: 'scale(-1,-1)',
  br: 'scaleY(-1)',
  tl: 'scaleX(-1)',
};

const MAIN_DEGREES = [90, 108, 126, 144, 162, 180];
const MAIN_RINGS = [0.16, 0.31, 0.48, 0.65, 0.84];
const MAIN_SIZE = 160;

const INTERACTIVE = 'button,a,input,select,textarea,label,[role=button],[role=tab],[role=menuitem],[role=checkbox],.rbc-event,.wbh-spider,.wbh-pumpkin';

const pointAt = (cx: number, cy: number, angle: number, radius: number): [number, number] => [
  cx + Math.cos(angle) * radius,
  cy + Math.sin(angle) * radius,
];

const webPath = (size: number, degrees: number[], rings: number[]): string => {
  const angles = degrees.map(degreesValue => (degreesValue * Math.PI) / 180);
  let path = '';
  angles.forEach(angle => {
    const [x, y] = pointAt(size, 0, angle, size);
    path += `M${size} 0L${x.toFixed(1)} ${y.toFixed(1)}`;
  });
  rings.forEach((fraction, ringIndex) => {
    const radius = fraction * size;
    for (let index = 0; index < angles.length - 1; index += 1) {
      const [x1, y1] = pointAt(size, 0, angles[index], radius + (index % 2) * 2);
      const [x2, y2] = pointAt(size, 0, angles[index + 1], radius + ((index + 1) % 2) * 2);
      const [cx, cy] = pointAt(size, 0, (angles[index] + angles[index + 1]) / 2, radius * (0.82 - ringIndex * 0.01));
      path += `M${x1.toFixed(1)} ${y1.toFixed(1)}Q${cx.toFixed(1)} ${cy.toFixed(1)} ${x2.toFixed(1)} ${y2.toFixed(1)}`;
    }
  });
  return path;
};

const GhostSvg = () => (
  <svg viewBox="0 0 40 48" aria-hidden="true">
    <path d="M4 20a16 16 0 0 1 32 0v26l-5-4-5 4-6-4-6 4-5-4-5 4z" fill="#ffffff" stroke="#c9c9c9" strokeWidth="1" />
    <circle cx="14" cy="20" r="2.5" fill="#333" />
    <circle cx="26" cy="20" r="2.5" fill="#333" />
    <ellipse cx="20" cy="29" rx="3" ry="4" fill="#333" />
  </svg>
);

const Bat = ({ flight, onDone }: { flight: BatFlight; onDone: (id: number) => void }) => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const element = ref.current;
    if (!element) {
      return undefined;
    }
    const width = window.innerWidth;
    const frames = Array.from({ length: 11 }, (_, index) => {
      const x = width + 40 - ((width + 120) * index) / 10;
      const y = flight.y0 + Math.sin(index * 1.3) * 30;
      return { transform: `translate(${x}px, ${y}px)` };
    });
    let alive = true;
    const animation = element.animate(frames, {
      delay: flight.delay,
      duration: flight.duration,
      easing: 'linear',
      fill: 'backwards',
    });
    const finish = () => {
      if (alive) {
        onDone(flight.id);
      }
    };
    animation.onfinish = finish;
    animation.oncancel = finish;
    return () => {
      alive = false;
      animation.cancel();
    };
  }, [flight, onDone]);

  return (
    <div ref={ref} className="wbh-layer wbh-bat">
      <svg viewBox="0 0 40 18" aria-hidden="true">
        <path d="M20 7Q14-1 3 4q4 3 2 7 6-3 15 3 9-6 15-3-2-4 2-7Q26-1 20 7z" fill="#1e1e1e" />
      </svg>
    </div>
  );
};

const DriftingGhost = ({ x, onDone }: { x: number; onDone: () => void }) => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const element = ref.current;
    if (!element) {
      return undefined;
    }
    const height = window.innerHeight;
    let alive = true;
    const animation = element.animate([
      { opacity: 0, transform: `translate(${x}px, ${height}px)` },
      { opacity: 0.85, offset: 0.3, transform: `translate(${x + 30}px, ${height * 0.6}px)` },
      { opacity: 0.7, offset: 0.7, transform: `translate(${x - 20}px, ${height * 0.3}px)` },
      { opacity: 0, transform: `translate(${x + 10}px, ${height * 0.05}px)` },
    ], { duration: 9000, easing: 'ease-in-out' });
    const finish = () => {
      if (alive) {
        onDone();
      }
    };
    animation.onfinish = finish;
    animation.oncancel = finish;
    return () => {
      alive = false;
      animation.cancel();
    };
  }, [onDone, x]);

  return (
    <div ref={ref} className="wbh-layer wbh-ghost" style={{ left: 0, top: 0 }}>
      <GhostSvg />
    </div>
  );
};

const BooGhost = ({ onDone }: { onDone: () => void }) => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const element = ref.current;
    if (!element) {
      return undefined;
    }
    let alive = true;
    const animation = element.animate(
      [{ opacity: 0 }, { opacity: 1, offset: 0.2 }, { opacity: 1, offset: 0.7 }, { opacity: 0 }],
      { duration: 1800, easing: 'ease-in-out' },
    );
    const finish = () => {
      if (alive) {
        onDone();
      }
    };
    animation.onfinish = finish;
    animation.oncancel = finish;
    return () => {
      alive = false;
      animation.cancel();
    };
  }, [onDone]);

  return (
    <div ref={ref} className="wbh-layer wbh-boo">
      <GhostSvg />
      <span>Boo!</span>
    </div>
  );
};

const Spider = () => {
  const [fled, setFled] = useState(false);
  const anchorAngle = (MAIN_DEGREES[2] * Math.PI) / 180;
  const [anchorX, anchorY] = pointAt(MAIN_SIZE, 0, anchorAngle, 50);
  const sy = anchorY + 46;
  const legs = [-1, 1]
    .map(side => `M${anchorX + 4 * side} ${sy}l${7 * side} -6M${anchorX + 5 * side} ${sy + 2}l${8 * side} -1M${anchorX + 5 * side} ${sy + 4}l${7 * side} 4M${anchorX + 4 * side} ${sy + 6}l${5 * side} 7`)
    .join('');

  useEffect(() => {
    if (!fled) {
      return undefined;
    }
    const timer = window.setTimeout(() => setFled(false), 60000);
    return () => window.clearTimeout(timer);
  }, [fled]);

  return (
    <g
      className={fled ? 'wbh-spider wbh-fled' : 'wbh-spider'}
      data-testid="workboard-halloween-spider"
      onClick={event => {
        event.stopPropagation();
        event.preventDefault();
        if (!fled) {
          setFled(true);
        }
      }}
    >
      <g className="wbh-spider-bob">
        <line className="wbh-thread" x1={anchorX} y1={anchorY - 200} x2={anchorX} y2={sy - 4} />
        <circle cx={anchorX} cy={sy + 2} r="5.5" fill="#1e1e1e" />
        <circle cx={anchorX} cy={sy - 4} r="3.4" fill="#1e1e1e" />
        <path d={legs} stroke="#1e1e1e" strokeWidth="1.2" fill="none" strokeLinecap="round" />
        <circle cx={anchorX - 1.2} cy={sy - 4.5} r="0.8" fill="#ff6a00" />
        <circle cx={anchorX + 1.2} cy={sy - 4.5} r="0.8" fill="#ff6a00" />
        <rect x={anchorX - 14} y={sy - 14} width="28" height="32" fill="transparent" />
      </g>
    </g>
  );
};

const WorkboardHalloweenLayer = () => {
  const halloween = useWorkboardHalloweenState();
  const { zoomLevel } = useZoom();
  const navOffsetPx = PRIMARY_NAVIGATION_HEIGHT_PX * getLegacyShellZoomCompensation() * zoomLevel;
  const webRefs = useRef<Partial<Record<WebCorner, HTMLDivElement | null>>>({});
  const tornRef = useRef<ReadonlySet<WebCorner>>(new Set());
  const launchBatRef = useRef<(delay: number) => void>(() => {});
  const nextBatId = useRef(0);
  const ghostDone = useRef(false);
  const [torn, setTorn] = useState<ReadonlySet<WebCorner>>(new Set());
  const [bats, setBats] = useState<BatFlight[]>([]);
  const [ghost, setGhost] = useState<{ x: number } | null>(null);
  const [booId, setBooId] = useState(0);

  tornRef.current = torn;

  const removeBat = useCallback((id: number) => {
    setBats(current => current.filter(bat => bat.id !== id));
  }, []);

  const clearGhost = useCallback(() => setGhost(null), []);
  const clearBoo = useCallback(() => setBooId(0), []);

  launchBatRef.current = (delay: number) => {
    const id = nextBatId.current;
    nextBatId.current += 1;
    setBats(current => [...current, {
      id,
      delay,
      duration: 6000 + Math.random() * 2000,
      y0: navOffsetPx + 20 + Math.random() * 140,
    }]);
  };

  useEffect(() => {
    if (halloween.still) {
      setBats([]);
    }
  }, [halloween.still]);

  useEffect(() => {
    if (!halloween.enabled) {
      return undefined;
    }
    let timer = 0;
    let cancelled = false;
    const schedule = () => {
      const wait = ((halloween.level === 'full' ? 90 : 240) + Math.random() * 150) * 1000;
      timer = window.setTimeout(() => {
        if (cancelled) {
          return;
        }
        const batsOn = halloween.level === 'full' ? halloween.days <= SHOW.bats : halloween.days === 0;
        if (!halloween.still && !document.hidden && batsOn) {
          launchBatRef.current(0);
        }
        schedule();
      }, wait);
    };
    schedule();
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [halloween.days, halloween.enabled, halloween.level, halloween.still]);

  useEffect(() => {
    if (!halloween.enabled || halloween.still || halloween.days > SHOW.ghost || ghostDone.current) {
      return undefined;
    }
    const timer = window.setTimeout(() => {
      ghostDone.current = true;
      setGhost({ x: 40 + Math.random() * 200 });
    }, 20000);
    return () => window.clearTimeout(timer);
  }, [halloween.days, halloween.enabled, halloween.still]);

  useEffect(() => {
    if (!halloween.enabled) {
      return undefined;
    }
    let typed = '';
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.ctrlKey || event.metaKey || event.altKey || event.key.length !== 1) {
        return;
      }
      const target = event.target as HTMLElement | null;
      if (target && (target.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName))) {
        return;
      }
      typed = (typed + event.key.toLowerCase()).slice(-3);
      if (typed !== 'boo') {
        return;
      }
      typed = '';
      setBooId(id => id + 1);
      const root = document.documentElement;
      if (halloween.still) {
        root.setAttribute('data-wbh-glow', '');
        window.setTimeout(() => root.removeAttribute('data-wbh-glow'), 1200);
        return;
      }
      root.setAttribute('data-wbh-shiver', '');
      window.setTimeout(() => root.removeAttribute('data-wbh-shiver'), 600);
      for (let index = 0; index < 3; index += 1) {
        launchBatRef.current(index * 350);
      }
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      document.documentElement.removeAttribute('data-wbh-shiver');
      document.documentElement.removeAttribute('data-wbh-glow');
    };
  }, [halloween.enabled, halloween.still]);

  useEffect(() => {
    if (!halloween.enabled) {
      return undefined;
    }
    const hits = new Map<WebCorner, number[]>();
    const timers: number[] = [];
    const onClick = (event: MouseEvent) => {
      if (!event.isTrusted) {
        return;
      }
      const target = event.target instanceof Element ? event.target : null;
      if (target?.closest(INTERACTIVE)) {
        return;
      }
      (['tr', 'bl', 'br', 'tl'] as const).forEach(corner => {
        const web = webRefs.current[corner];
        if (!web || tornRef.current.has(corner)) {
          return;
        }
        const rect = web.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) {
          return;
        }
        const originX = corner === 'bl' || corner === 'tl' ? rect.left : rect.right;
        const originY = corner === 'bl' || corner === 'br' ? rect.bottom : rect.top;
        if (Math.hypot(event.clientX - originX, event.clientY - originY) > rect.width) {
          return;
        }
        const now = Date.now();
        const recent = (hits.get(corner) ?? []).filter(time => now - time < 1500);
        recent.push(now);
        hits.set(corner, recent);
        if (recent.length < 3) {
          return;
        }
        hits.delete(corner);
        setTorn(current => {
          const next = new Set(current);
          next.add(corner);
          return next;
        });
        timers.push(window.setTimeout(() => {
          setTorn(current => {
            const next = new Set(current);
            next.delete(corner);
            return next;
          });
        }, 90000));
      });
    };
    document.addEventListener('click', onClick, { capture: true, passive: true });
    return () => {
      document.removeEventListener('click', onClick, { capture: true });
      timers.forEach(timer => window.clearTimeout(timer));
    };
  }, [halloween.enabled]);

  if (!halloween.enabled || typeof document === 'undefined') {
    return null;
  }

  const showSpider = halloween.days <= SHOW.spider;
  const mainOpacity = 0.25 + 0.35 * Math.min(1, (SHOW.web - halloween.days) / 6);

  return createPortal(
    <>
      {halloween.days <= SHOW.web && (
        <div
          ref={node => {
            webRefs.current.tr = node; 
          }}
          className={torn.has('tr') ? 'wbh-layer wbh-web wbh-torn' : 'wbh-layer wbh-web'}
          style={{ top: navOffsetPx, opacity: mainOpacity }}
        >
          <svg width={MAIN_SIZE} height={MAIN_SIZE} viewBox={`0 0 ${MAIN_SIZE} ${MAIN_SIZE}`} aria-hidden="true">
            <path d={webPath(MAIN_SIZE, MAIN_DEGREES, MAIN_RINGS)} />
            {showSpider && <Spider />}
          </svg>
        </div>
      )}
      {EXTRA_WEBS.filter(web => halloween.days <= web.days).map(web => (
        <div
          key={web.corner}
          ref={node => {
            webRefs.current[web.corner] = node; 
          }}
          className={torn.has(web.corner) ? `wbh-layer wbh-web wbh-web-${web.corner} wbh-torn` : `wbh-layer wbh-web wbh-web-${web.corner}`}
          style={{
            bottom: web.corner === 'tl' ? 'auto' : 0,
            left: web.corner === 'br' ? 'auto' : 0,
            right: web.corner === 'br' ? 0 : 'auto',
            top: web.corner === 'tl' ? navOffsetPx : 'auto',
            transform: MIRROR[web.corner],
          }}
        >
          <svg width={web.size} height={web.size} viewBox={`0 0 ${web.size} ${web.size}`} aria-hidden="true">
            <path d={webPath(web.size, web.degrees, web.rings)} />
          </svg>
        </div>
      ))}
      {bats.map(flight => <Bat key={flight.id} flight={flight} onDone={removeBat} />)}
      {ghost && <DriftingGhost x={ghost.x} onDone={clearGhost} />}
      {booId > 0 && <BooGhost key={booId} onDone={clearBoo} />}
    </>,
    document.body,
  );
};

export default WorkboardHalloweenLayer;
